import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Video, UploadCloud, Trash2, Crown, Lock, Camera } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import WebcamRecorder from "@/components/WebcamRecorder";

const PREMIUM_PLANS = new Set(["premium", "vip"]);

export default function VideoUploader({ user, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const isPremium = PREMIUM_PLANS.has(user?.plan) || user?.role === "admin";

  const choose = () => {
    if (!isPremium) return;
    inputRef.current?.click();
  };

  const record = () => {
    if (!isPremium) return;
    setRecorderOpen(true);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Video must be under 50MB. Trim to ~60 seconds for the best impression.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Video introduction uploaded");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadFile(file);
  };

  const remove = async () => {
    if (!window.confirm("Remove your video introduction?")) return;
    try {
      await api.delete("/profile/video");
      toast.success("Video removed");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!isPremium) {
    return (
      <div className="border border-brand-gold/40 bg-brand-gold/5 rounded-lg p-5" data-testid="video-uploader-locked">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-brand-gold" />
          <p className="overline">Premium feature</p>
        </div>
        <h3 className="font-serif text-xl text-brand-ink mt-2">Add a video introduction</h3>
        <p className="text-sm text-brand-inkLight mt-1">A 30–60 second video doubles a candidate&apos;s likelihood of receiving an interest. Upload yours with Premium or VIP.</p>
        <Link to="/plans" className="btn-gold inline-flex items-center gap-2 mt-4 text-sm py-2"><Crown className="h-4 w-4" /> Upgrade to unlock</Link>
      </div>
    );
  }

  return (
    <div className="border border-brand-line bg-white rounded-lg p-5" data-testid="video-uploader">
      <div className="flex items-center gap-2">
        <Video className="h-4 w-4 text-brand-gold" />
        <p className="overline">Video introduction</p>
      </div>
      <h3 className="font-serif text-xl text-brand-ink mt-1">Your 60-second pitch</h3>
      <p className="text-xs text-brand-inkLight mt-1">MP4, WebM or MOV · up to 50MB · ~60 seconds is ideal.</p>

      {user?.video_url ? (
        <>
          <video
            src={fileUrl(user.video_url)}
            controls
            preload="metadata"
            className="mt-4 w-full rounded-md bg-brand-ink/5 max-h-72"
            data-testid="profile-video-preview"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={record} disabled={uploading} className="btn-primary text-sm py-2 flex items-center gap-2" data-testid="video-record-replace">
              <Camera className="h-4 w-4" /> Re-record
            </button>
            <button onClick={choose} disabled={uploading} className="btn-ghost text-sm py-2 flex items-center gap-2" data-testid="video-replace">
              <UploadCloud className="h-4 w-4" /> Upload new file
            </button>
            <button onClick={remove} disabled={uploading} className="text-xs text-brand-maroon hover:underline flex items-center gap-1 ml-auto" data-testid="video-remove">
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
        </>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <button
            onClick={record}
            disabled={uploading}
            className="border-2 border-dashed border-brand-line hover:border-brand-maroon rounded-md py-6 flex flex-col items-center justify-center gap-2 transition-colors group"
            data-testid="video-record-btn"
          >
            <Camera className="h-6 w-6 text-brand-inkMute group-hover:text-brand-maroon transition-colors" strokeWidth={1.4} />
            <span className="text-sm text-brand-ink font-medium">Record with webcam</span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-brand-inkMute">Up to 60 seconds</span>
          </button>
          <button
            onClick={choose}
            disabled={uploading}
            className="border-2 border-dashed border-brand-line hover:border-brand-gold rounded-md py-6 flex flex-col items-center justify-center gap-2 transition-colors group"
            data-testid="video-upload-btn"
          >
            <UploadCloud className="h-6 w-6 text-brand-inkMute group-hover:text-brand-gold transition-colors" strokeWidth={1.4} />
            <span className="text-sm text-brand-ink font-medium">{uploading ? `Uploading… ${progress}%` : "Upload a file"}</span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-brand-inkMute">MP4 / WebM · up to 50MB</span>
          </button>
        </div>
      )}

      {uploading && (
        <div className="mt-3 h-1 bg-brand-muted rounded-full overflow-hidden">
          <div className="h-full bg-brand-gold transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
        className="hidden"
        onChange={onPick}
        data-testid="video-file-input"
      />

      <WebcamRecorder
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onConfirm={(file) => { setRecorderOpen(false); uploadFile(file); }}
      />
    </div>
  );
}
